/**
 * Which columns a view reads, and how it reads them.
 *
 * Five of the seven view types draw nothing until they are told which columns
 * carry the thing they are about -- a date, a select to stack by, a file to put
 * on a card, the two ends of a list. One hook for all of them, because it is
 * one `PATCH` to the view and the only thing that differs is which key is in
 * the body.
 *
 * Applied optimistically, because the answer is known before it is asked for
 * and a control that waits a round trip to show what was chosen reads as
 * broken. The answer is not written back: the endpoint serialises a view
 * *without* its filters and sortings, so storing it would quietly unfilter the
 * view.
 *
 * Unlike the grid's own settings next door, these **do** change which rows come
 * back -- a schedule asks for a month of its date column, a list for the rows
 * that are in one of its lists -- but nothing here has to arrange that: the
 * view is the row query's signature, so patching the cached view is what
 * re-asks.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { View } from '@/api/types'
import type { Timescale } from '../gantt/timeline'
import { patchView } from './view-cache'

/** What a view can be told, each key absent unless it is being set. */
export interface ViewSettings {
  /** A schedule's date column. */
  date_field?: number | null
  /** A timeline's left end. */
  start_date_field?: number | null
  /** A timeline's right end. */
  end_date_field?: number | null
  /** How wide a timeline's columns are. */
  timescale?: Timescale
  /** The single select a board stacks by. */
  single_select_field?: number | null
  /** The file column a board or a gallery puts on its cards. */
  card_cover_image_field?: number | null
  /** The column in a list view's left column. */
  member_field?: number | null
  /** What to show of a linked member row, instead of its primary column. */
  member_target_field?: number | null
  /** The column supplying a list view's lists. */
  list_field?: number | null
  /** What to show of a linked list row, instead of its primary column. */
  list_target_field?: number | null
  /** Whether a list view shows every one of its list field's values. */
  include_all_list_values?: boolean
  /**
   * The list values shown when not all of them are.
   *
   * Mutable, unlike every read type here, because it is written into the
   * cached view -- which the compiler holds to the shape the API answers with.
   */
  allowed_list_values?: number[]
}

export interface ViewSettingsMutations {
  choose: (settings: ViewSettings) => void
  isPending: boolean
}

export function useViewSettings(tableId: number, viewId: number): ViewSettingsMutations {
  const queryClient = useQueryClient()
  const key = qk.views(tableId)

  const change = useMutation({
    mutationFn: (settings: ViewSettings): Promise<unknown> =>
      unwrap(
        getApi().PATCH('/api/database/views/{view_id}/', {
          params: { path: { view_id: viewId } },
          // Cast at the boundary, like every view payload: the body is a union
          // over the view types and the compiler cannot know which one an id
          // names.
          body: settings as never,
        }),
      ),

    onMutate: (settings) => {
      const previous = queryClient.getQueryData<View[]>(key)
      queryClient.setQueryData<View[]>(key, (views) =>
        views === undefined
          ? views
          : patchView(views, viewId, (view) => ({ ...view, ...settings })),
      )
      return { previous }
    },

    onError: (_error, _settings, context) => {
      queryClient.setQueryData<View[] | undefined>(key, context?.previous)
    },
  })

  return {
    choose: (settings) => {
      change.mutate(settings)
    },
    isPending: change.isPending,
  }
}
